import { AssetRequestError, getAssetById } from '../api/getAssetById';
import { isIpMismatchAuthError } from '../api/httpErrorFormat';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pickTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Whether a list/GET asset row is treated as bookable for clients. */
export function assetRecordIsBookable(asset: Record<string, unknown>): boolean {
  const raw =
    asset.asset_status ??
    asset.status ??
    asset.assetStatus ??
    asset.AssetStatus;
  if (typeof raw === 'boolean') {
    return raw;
  }
  const label = pickTrimmedString(raw).toLowerCase();
  if (!label) {
    return true;
  }
  if (/inactive|delist|unlisted|removed|deactivated|archived|disabled|unavailable/.test(label)) {
    return false;
  }
  if (raw === 0 || raw === '0' || label === 'false') {
    return false;
  }
  return true;
}

/** Wire status for public browse (`POST /search/assets` filters `asset_status: ['active']`). */
export const ASSET_STATUS_BOOKABLE = 'active';

/** Preferred wire status that hides the asset from browse/search while keeping it on the owner list. */
export const ASSET_STATUS_UNAVAILABLE = 'inactive';

/** Status values tried when making an asset bookable (first match that persists wins). */
export const ASSET_STATUS_BOOKABLE_CANDIDATES = ['active', 'published', 'available'] as const;

/** Status values tried when hiding an asset from browse (servers vary: inactive / unavailable / delisted). */
export const ASSET_STATUS_UNAVAILABLE_CANDIDATES = ['inactive', 'unavailable', 'delisted'] as const;

export function pickAssetStatusLabel(asset: Record<string, unknown>): string {
  const raw =
    asset.asset_status ??
    asset.status ??
    asset.assetStatus ??
    asset.AssetStatus;
  return pickTrimmedString(raw);
}

export function assetRecordBookingAvailability(asset: Record<string, unknown>): boolean {
  return assetRecordIsBookable(asset);
}

/** Set whether clients can find/book this asset (owner edit / create). */
export function withAssetBookingAvailability(
  asset: Record<string, unknown>,
  available: boolean,
): Record<string, unknown> {
  return {
    ...asset,
    asset_status: available ? ASSET_STATUS_BOOKABLE : ASSET_STATUS_UNAVAILABLE,
  };
}

export class AssetBookingAvailabilityPersistError extends Error {
  readonly triedStatuses: readonly string[];

  constructor(triedStatuses: readonly string[]) {
    super('ASSET_STATUS_NOT_PERSISTED');
    this.name = 'AssetBookingAvailabilityPersistError';
    this.triedStatuses = triedStatuses;
  }
}

type PatchAssetBodyFn = (assetId: string, body: Record<string, unknown>) => Promise<void>;
type LoadAssetFn = (assetId: string) => Promise<Record<string, unknown>>;

/** Wire body shapes tried when stacks differ on the status field name. */
export function assetStatusPatchBodies(status: string): Record<string, unknown>[] {
  return [{ asset_status: status }, { status: status }, { assetStatus: status }];
}

/**
 * PATCH status then GET to confirm. Tries alternate field names and enum values used by different
 * Offizz stacks when the preferred value is accepted but ignored.
 */
export async function persistAssetBookingAvailability(options: {
  assetId: string;
  available: boolean;
  /** Preferred: send a PATCH body. Falls back to `patchStatus` if omitted. */
  requestPatch?: PatchAssetBodyFn;
  /** @deprecated Prefer `requestPatch` — sends `{ asset_status }` only. */
  patchStatus?: (assetId: string, status: string) => Promise<void>;
  loadAsset: LoadAssetFn;
}): Promise<{ status: string; asset: Record<string, unknown> }> {
  const candidates = options.available
    ? ASSET_STATUS_BOOKABLE_CANDIDATES
    : ASSET_STATUS_UNAVAILABLE_CANDIDATES;
  const tried: string[] = [];
  const sendPatch =
    options.requestPatch ??
    (options.patchStatus
      ? async (assetId: string, body: Record<string, unknown>) => {
          const status = pickTrimmedString(body.asset_status ?? body.status ?? body.assetStatus);
          if (!status) {
            throw new Error('ASSET_STATUS_PATCH_EMPTY');
          }
          await options.patchStatus!(assetId, status);
        }
      : null);
  if (!sendPatch) {
    throw new Error('ASSET_STATUS_PATCH_FN_REQUIRED');
  }

  for (const status of candidates) {
    for (const body of assetStatusPatchBodies(status)) {
      const label = `${Object.keys(body)[0]}=${status}`;
      tried.push(label);
      await sendPatch(options.assetId, body);
      const fresh = await options.loadAsset(options.assetId);
      if (assetRecordIsBookable(fresh) === options.available) {
        return {
          status: pickAssetStatusLabel(fresh) || status,
          asset: fresh,
        };
      }
    }
  }

  throw new AssetBookingAvailabilityPersistError(tried);
}

export function isAssetNotBookableApiMessage(message: string): boolean {
  return /asset not found|delisted|not bookable|no longer available/i.test(message.trim());
}

/**
 * Merge a search/list snapshot with a GET /assets/:id payload.
 * Keeps browse pricing / ids when GET is sparse or client-restricted responses omit fields.
 */
export function mergeBookingAssetSnapshot(
  snapshot: Record<string, unknown> | null | undefined,
  fresh: Record<string, unknown>,
): Record<string, unknown> {
  if (!snapshot) {
    return fresh;
  }
  const snapMethods = snapshot.pricing_methods;
  const freshMethods = fresh.pricing_methods;
  const pricing_methods =
    Array.isArray(freshMethods) && freshMethods.length > 0
      ? freshMethods
      : Array.isArray(snapMethods) && snapMethods.length > 0
        ? snapMethods
        : freshMethods ?? snapMethods;
  const opening_times =
    fresh.opening_times !== undefined && fresh.opening_times !== null
      ? fresh.opening_times
      : snapshot.opening_times;
  const merged: Record<string, unknown> = {
    ...snapshot,
    ...fresh,
  };
  if (pricing_methods !== undefined) {
    merged.pricing_methods = pricing_methods;
  }
  if (opening_times !== undefined) {
    merged.opening_times = opening_times;
  }

  // Never let a sparse GET wipe the bookable search row identity or active status.
  const snapId =
    snapshot.id ?? snapshot.asset_id ?? snapshot.assetId ?? snapshot.Id ?? snapshot.UUID;
  const mergedId = merged.id ?? merged.asset_id ?? merged.assetId ?? merged.Id ?? merged.UUID;
  const snapIdOk =
    (typeof snapId === 'string' && snapId.trim().length > 0) ||
    (typeof snapId === 'number' && Number.isFinite(snapId));
  const mergedIdOk =
    (typeof mergedId === 'string' && mergedId.trim().length > 0) ||
    (typeof mergedId === 'number' && Number.isFinite(mergedId));
  if (snapIdOk && !mergedIdOk) {
    merged.id = snapId;
    merged.asset_id = snapId;
  }

  const snapStatus =
    snapshot.asset_status ?? snapshot.status ?? snapshot.assetStatus ?? snapshot.AssetStatus;
  if (assetRecordIsBookable(snapshot) && !assetRecordIsBookable(merged) && snapStatus !== undefined) {
    merged.asset_status = snapStatus;
    if ('status' in merged && !assetRecordIsBookable(merged)) {
      merged.status = snapStatus;
    }
  }

  return merged;
}

/**
 * Fresh GET /assets/:id before booking — rejects assets the API returns as not bookable.
 *
 * HTTP failures (404, 401 IP mismatch, network, …) are rethrown as {@link AssetRequestError}.
 * Callers with a search/list snapshot should use {@link resolveAssetForBookingCreate} so a
 * transient or client-restricted GET does not look like “asset removed from the list”.
 */
export async function loadAssetForBookingCreate(assetId: string): Promise<Record<string, unknown>> {
  const trimmed = assetId.trim();
  if (!trimmed) {
    throw new Error('ASSET_NOT_BOOKABLE');
  }
  let loaded: unknown;
  try {
    loaded = await getAssetById(trimmed);
  } catch {
    throw new Error('ASSET_NOT_BOOKABLE');
  }
  if (!isPlainObject(loaded)) {
    throw new Error('ASSET_NOT_BOOKABLE');
  }
  if (!assetRecordIsBookable(loaded)) {
    throw new Error('ASSET_NOT_BOOKABLE');
  }
  return loaded;
}

/**
 * Prefer a fresh GET merged onto the search/list snapshot. If refresh fails for any reason
 * — including GET “delisted/not found” for client tokens — use the snapshot when it still
 * looks bookable. Search already filtered `asset_status: ['active']`; clients often cannot
 * GET /assets/:id, so that must never block Proceed.
 */
export async function resolveAssetForBookingCreate(
  assetId: string,
  snapshot?: Record<string, unknown> | null,
): Promise<Record<string, unknown>> {
  const snapshotOk = Boolean(snapshot && assetRecordIsBookable(snapshot));
  try {
    const fresh = await loadAssetForBookingCreate(assetId);
    return mergeBookingAssetSnapshot(snapshot, fresh);
  } catch (err) {
    if (snapshotOk && snapshot) {
      return snapshot;
    }
    throw err;
  }
}

export function bookingAssetLoadFailureMessage(
  err: unknown,
  messages: {
    notBookable: string;
    session: string;
    ipMismatch: string;
  },
): string {
  if (err instanceof Error && err.message === 'ASSET_NOT_BOOKABLE') {
    return messages.notBookable;
  }
  if (err instanceof AssetRequestError) {
    const haystack = `${err.rawBody ?? ''}\n${err.errorDescription ?? ''}\n${err.message ?? ''}`;
    if (err.httpStatus === 401 || err.httpStatus === 403) {
      if (isIpMismatchAuthError(haystack)) {
        return messages.ipMismatch;
      }
      return messages.session;
    }
  }
  if (err instanceof Error && err.message.trim()) {
    return err.message;
  }
  return messages.notBookable;
}
